"use client";

import { motion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import Link from "next/link";

import { Container } from "@/app/(marketing)/_components/ui/container";
import type { McpAppDirectory } from "@/config/mcp-app-directories";

import { DirectoryIcon } from "./directory-icon";

/**
 * Hero for a directory landing page (/chatgpt, /claude).
 *
 * Deliberately not /mcp's hero. That page opens on "the expert labor MCP" and
 * sells an integration to someone building an agent. A directory sends a
 * researcher, a PM, or a founder who wants to talk to customers and has no
 * reason to identify as an agent builder, so this one leads with the job rather
 * than the protocol, and the only primary action is installing the app.
 */
export function AppDirectoryHero({
  directory,
  headline,
  subhead,
}: {
  directory: McpAppDirectory;
  headline: string;
  subhead: string;
}) {
  return (
    <section className="relative overflow-hidden pt-16 pb-32 md:pt-24 md:pb-40">
      <Container>
        <motion.div
          initial={{ opacity: 0, transform: "translateY(20px)" }}
          animate={{ opacity: 1, transform: "translateY(0px)" }}
          transition={{ duration: 0.5 }}
          className="relative z-10 mx-auto max-w-[860px] text-center"
        >
          <div className="mb-6 flex justify-center">
            <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 backdrop-blur-sm">
              <DirectoryIcon
                id={directory.id}
                className="h-3.5 w-3.5 text-white/80"
              />
              <p className="font-mono text-sm tracking-[-0.28px] text-white/80">
                Now in the {directory.name} app directory
              </p>
            </div>
          </div>

          <h1 className="mb-6 text-4xl font-medium tracking-tighter text-balance text-white sm:text-[56px] sm:leading-[1.1] md:text-[64px] md:leading-[1.1]">
            {headline}
          </h1>

          <p className="mx-auto mb-12 max-w-[700px] text-lg text-balance text-white/80">
            {subhead}
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={directory.listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-marketing-blue group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium transition-colors hover:bg-white/90"
            >
              <DirectoryIcon id={directory.id} className="h-4 w-4" />
              Add to {directory.name}
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <Link
              href="#how-it-works"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
            >
              See how it works
            </Link>
          </div>

          <p className="mt-6 text-sm text-white/60">
            Free to set up and price a study. Nothing is charged until one
            launches.
          </p>
        </motion.div>
      </Container>
    </section>
  );
}
